<script lang="ts">
	import type { Icon } from '#library/types.ts'
	import type { Snippet } from 'svelte'

	// TODO: normalise icon sizes in vector.svg

	interface Props {
		class?: string
		href?: string
		onclick?: () => void
		children?: Snippet
		outline?: boolean
		text?: boolean
		large?: boolean
		fill?: boolean
		disabled?: boolean
		type?: 'button' | 'submit' | 'reset'
		icon?: Icon
		loading?: boolean
		label?: string
		target?: string
	}

	let {
		class: classname = undefined,
		href = undefined,
		onclick = undefined,
		children = undefined,
		outline = false,
		text = false,
		large = false,
		fill = false,
		type = 'button',
		icon = undefined,
		loading = false,
		label = undefined,
		target = undefined,
		...props
	}: Props = $props()

	const disabled = $derived(props.disabled === true || loading || undefined)
</script>

<svelte:element
	this={href ? 'a' : 'button'}
	class="button {classname}"
	class:outline
	class:text
	class:large
	class:fill
	class:icon={!children}
	{href}
	type={href ? undefined : type}
	{onclick}
	role={href ? 'link' : 'button'}
	{disabled}
	aria-label={label}
	title={label}
	{target}
>
	{#if children}
		<span class="content" class:hide={loading}>{@render children?.()}</span>
	{/if}
	{#if icon}
		<svg class="svg">
			<use xlink:href="/images/vector.svg#{icon || 'arrow-right'}"></use>
		</svg>
	{/if}
	{#if loading}
		<svg class="spinner svg" aria-hidden="true" role="status">
			<use xlink:href="/images/vector.svg#spinner"></use>
		</svg>
	{/if}
</svelte:element>

<style lang="css">
	.button {
		align-items: center;
		background-color: var(--c-foreground, var(--c-crocus));
		border-radius: var(--r-xxs);
		color: var(--c-background, var(--c-gimson-light));
		font: 500 var(--f-body2);
		height: 3rem;
		justify-content: center;
		letter-spacing: var(--ls-body2);
		padding-inline: 1.5rem;
		display: inline-flex;
		gap: 0.5rem;
		text-align: center;
		/* a keyboard focus ring in the button's own colour, drawn in from further out */
		outline: 1.5px solid transparent;
		outline-offset: 6px;
		transition:
			background-color 0.2s ease,
			color 0.2s ease,
			outline-color 0.2s ease,
			outline-offset 0.2s ease;
		white-space: nowrap;

		&:focus-visible {
			outline-color: var(--c-foreground, var(--c-crocus));
			outline-offset: 3px;
		}

		@media (--from-mobile) {
			height: 3.5rem;
			padding-inline: 2rem;
		}

		&::after {
			border: 1.5px solid currentColor;
			border-radius: inherit;
			content: '';
			inset: 0;
			opacity: 0;
			position: absolute;
			pointer-events: none;
			transition:
				opacity 0.2s ease,
				border-width 0.1s ease;
		}

		&:hover::after {
			border-width: 2.5px;
			opacity: 1;
		}

		&:active {
			background-color: var(--c-background, var(--c-gimson-light));
			color: var(--c-background, var(--c-crocus));
		}

		&[disabled] {
			opacity: 0.4;
			pointer-events: none;
		}

		&.outline {
			background-color: transparent;
			color: var(--c-foreground, var(--c-crocus));

			&::after {
				opacity: 1;
			}
		}

		&.large {
			font: 500 var(--f-body1);
			height: 3.5rem;
			letter-spacing: var(--ls-body1);
			padding-inline: 1.75rem 1.25rem;

			@media (--from-mobile) {
				height: 4.5rem;
				padding-inline: 2.5rem;
			}
		}

		&.text {
			background-color: transparent;
			color: var(--c-foreground, var(--c-crocus));
			height: auto;
			padding: 0;

			&::after {
				display: none;
			}

			&:hover {
				text-decoration: underline;
			}
		}

		&.icon {
			/* margin-inline-end: -0.5rem; */
			padding-inline: 0;
			size: 3rem;

			@media (--from-mobile) {
				size: 3.5rem;
			}

			&.large {
				size: 3.5rem;

				@media (--from-mobile) {
					size: 4.5rem;
				}
			}
		}

		&.fill {
			width: 100%;
		}
	}

	.content {
		display: inline-flex;
		gap: 0.5rem;
		transition:
			opacity 0.2s ease,
			translate 0.2s ease;

		&.hide {
			opacity: 0;
			translate: 0 -100%;
		}
	}

	@keyframes reveal {
		from {
			opacity: 0;
			translate: -50% 100%;
			scale: 0.5;
		}
		to {
			opacity: 1;
			translate: -50% -50%;
			scale: 1;
		}
	}

	.svg {
		size: 1.5rem;

		&.spinner {
			position: absolute;
			left: 50%;
			top: 50%;
			animation:
				spin 1s linear infinite,
				reveal 0.2s ease;
			opacity: 1;
			translate: -50% -50%;
		}
	}
</style>
